import { call, put, select, takeLatest } from 'redux-saga/effects'
import * as Sentry from '@sentry/react'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { runtimeConfig } from '@/runtime-config'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { buildPulsePageUrl, mountPulseWidget } from './pulseLoader'
import {
  activationRequested,
  activationSettled,
  dockActivationFailed,
  dockActivationRequested,
  dockActivationSucceeded,
} from './hopTimusSlice'

const ACTIVATION_ENDPOINT = `${runtimeConfig.APP_URL}/api/spa/shared/hop-timus/activation`
const END_SESSION_ENDPOINT = `${runtimeConfig.APP_URL}/api/spa/shared/hop-timus/end-session`

export const hopTimusActions = {
  ACTIVATE: 'HOP_TIMUS_ACTIVATE',
  ACTIVATE_DOCK: 'HOP_TIMUS_ACTIVATE_DOCK',
  END_SESSION: 'HOP_TIMUS_END_SESSION',
}

interface PulseActivation {
  activation_token: string
  space: string
  widget_url: string
}

function* requestActivation(pageUrl: string): Generator<any, PulseActivation | null, any> {
  const { authTokenSession } = yield select(selectAuthData)
  const config = yield call(getAPIConfig as any, { method: 'post', authTokenSession, body: { page_url: pageUrl } })
  const response = yield call(fetch, ACTIVATION_ENDPOINT, config)
  const { data } = yield call([response, response.json])

  if (response.status !== 200 || !data?.activation_token || !data?.space || !data?.widget_url) return null

  return data
}

export function* activateHopTimusSaga(action: any): Generator {
  const { pageUrl, unavailableMessage, showNotification } = action
  try {
    yield put(activationRequested())

    const data = (yield call(requestActivation, pageUrl)) as PulseActivation | null

    if (!data) {
      yield call(showNotification, { type: SNACKBAR_TYPES.ERROR, message: unavailableMessage })
      return
    }

    yield call(mountPulseWidget, data.widget_url, data.space, data.activation_token)
  } catch (error) {
    yield call(showNotification, { type: SNACKBAR_TYPES.ERROR, message: unavailableMessage })
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'activateHopTimusSaga' } })
    }
  } finally {
    yield put(activationSettled())
  }
}

export function* activateHopTimusDockSaga(action: { type: string; pageUrl: string }): Generator {
  const { pageUrl } = action
  try {
    yield put(dockActivationRequested())

    const data = (yield call(requestActivation, pageUrl)) as PulseActivation | null

    if (!data) {
      yield put(dockActivationFailed())
      return
    }

    yield put(dockActivationSucceeded(buildPulsePageUrl(data.widget_url, data.space, data.activation_token, pageUrl)))
  } catch (error) {
    yield put(dockActivationFailed())
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'activateHopTimusDockSaga' } })
    }
  }
}

// Best-effort: the frontend already purged its local state via destroyPulseWidget().
export function* endHopTimusSessionSaga(): Generator {
  try {
    const { authTokenSession } = yield select(selectAuthData)
    const config = yield call(getAPIConfig as any, { method: 'post', authTokenSession })

    yield call(fetch, END_SESSION_ENDPOINT, config)
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { context: 'endHopTimusSessionSaga' } })
    }
  }
}

export default function* hopTimusSagas() {
  yield takeLatest(hopTimusActions.ACTIVATE, activateHopTimusSaga)
  yield takeLatest(hopTimusActions.ACTIVATE_DOCK, activateHopTimusDockSaga)
  yield takeLatest(hopTimusActions.END_SESSION, endHopTimusSessionSaga)
}
